import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Paperclip } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { track, source } from "../../lib/track";
import { Field, Chip } from "./ExpressForm";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^\+?[0-9][0-9 .()-]{6,19}$/;
const KINDS = [["sur_mesure", "Création ou pièce spécifique"], ["evenement", "Animation broderie sur votre événement"], ["vetements", "Textiles que je fournis"]];

export function ProjectForm({ initialKind = "sur_mesure" }) {
  const { t } = useT();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [f, setF] = useState({ kind: initialKind, text: "", date: "", venue: "", participants: "", fabric: "", name: "", email: "", phone: "", company: "" });
  const [files, setFiles] = useState([]);
  const [errs, setErrs] = useState({});
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  const [subId] = useState(() => Math.random().toString(36).slice(2) + Date.now());
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const inp = (k, extra = {}) => ({ className: `k-input ${errs[k] ? "border-[#A84C2A]" : ""}`, value: f[k], onChange: (e) => set({ [k]: e.target.value }), "data-testid": `project-${k}`, ...extra });

  const submit = async (e) => {
    e.preventDefault();
    const v = {};
    if (f.text.trim().length < 10) v.text = t("Décrivez votre projet en quelques mots.");
    if (!f.name.trim()) v.name = t("Votre nom est nécessaire pour vous répondre.");
    if (!EMAIL_RE.test(f.email)) v.email = t("Indiquez un email valide.");
    if (!PHONE_RE.test(f.phone.trim())) v.phone = t("Indiquez un numéro valide, ex. 06 12 34 56 78 ou +33 6 12 34 56 78.");
    setErrs(v);
    if (Object.keys(v).length) return;
    setSending(true); setErr("");
    const payload = { offer: f.kind, submission_id: subId, hp: "", contact: { name: f.name, email: f.email, phone: f.phone, company: f.company }, source: { ...source(), project: true },
      items: f.kind === "vetements" ? [{ category: "Textile fourni par le client", quantity: 0, supply: "client", technique: "atelier", fabric_info: f.fabric, placements: [] }] : [],
      event: f.kind === "evenement" ? { date: f.date, venue: f.venue, participants: f.participants, brief: f.text } : {},
      custom: f.kind === "sur_mesure" ? { brief: f.text } : {},
      common: { comment: f.text, logo_later: files.length === 0 } };
    const fd = new FormData(); fd.append("payload", JSON.stringify(payload)); files.forEach((x) => fd.append("files", x));
    try {
      const { data } = await api.post("/requests", fd);
      track("form_submit", { offer: f.kind, project: true });
      nav("/demande/confirmation", { state: { ...data, email: f.email, offer: f.kind } });
    } catch (ex) { track("form_error", { offer: f.kind }); setErr(errMsg(ex) || t("L'envoi a échoué. Votre saisie est conservée : réessayez.")); } finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} noValidate className="k-card p-6 sm:p-8 grid gap-6 bg-[#FBF9F5]" data-testid="project-form">
      <div><h2 className="font-display text-2xl sm:text-3xl">{t("Parlez-nous de votre projet.")}</h2><p className="text-sm text-[#57534E] mt-1">{t("Réponse personnelle de l'atelier. Gratuit et sans engagement.")}</p></div>
      <div className="flex flex-wrap gap-2">{KINDS.map(([k, l]) => <Chip key={k} on={f.kind === k} testid={`project-kind-${k}`} onClick={() => set({ kind: k })}>{t(l)}</Chip>)}</div>
      <Field label={t("Votre projet *")} error={errs.text}><textarea rows={5} {...inp("text", { placeholder: t("Ce que vous imaginez, pour qui, pour quand, et ce qui compte pour vous.") })} /></Field>
      {f.kind === "evenement" && (
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label={t("Date de l'événement")}><input type="date" {...inp("date")} /></Field>
          <Field label={t("Lieu")}><input {...inp("venue", { placeholder: "Nice, Monaco…" })} /></Field>
          <Field label={t("Participants (environ)")}><input inputMode="numeric" {...inp("participants")} /></Field>
        </div>
      )}
      {f.kind === "vetements" && <Field label={t("Vos textiles (matière, marque, quantité)")}><input {...inp("fabric", { placeholder: t("ex. 30 polos coton déjà achetés") })} /></Field>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("Nom *")} error={errs.name}><input {...inp("name", { autoComplete: "name" })} /></Field>
        <Field label={t("Email *")} error={errs.email}><input type="email" {...inp("email", { autoComplete: "email" })} /></Field>
        <Field label={t("Téléphone *")} error={errs.phone}><input type="tel" {...inp("phone", { autoComplete: "tel" })} /><span className="text-xs text-[#8C857B]">{t("Pour vous rappeler et préciser votre projet si nécessaire.")}</span></Field>
        <Field label={t("Entreprise / organisation")}><input {...inp("company")} /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <button type="button" className="k-btn-outline k-btn-sm" onClick={() => fileRef.current?.click()} data-testid="project-files-btn"><Paperclip size={14} /> {files.length ? `${files.length} ${t("fichier(s)")}` : t("Joindre des fichiers (optionnel)")}</button>
        <input ref={fileRef} type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,.svg,.ai,.eps,image/*" className="hidden" onChange={(e) => setFiles([...e.target.files].slice(0, 8))} data-testid="project-files" />
      </div>
      {err && <p className="text-sm text-[#A84C2A]" role="alert" data-testid="project-error">{err}</p>}
      <button type="submit" className="k-btn self-start" disabled={sending} data-testid="project-submit">{sending ? t("Envoi…") : t("Envoyer mon projet")} <ArrowRight size={16} /></button>
    </form>
  );
}
